/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable, observable, ObservableMap } from 'mobx';
import { v4 as UUID } from 'uuid';
import { CoachWorkout } from './CoachWorkout';
import { LocalizedValue, createLocalizedValue } from '../../../LocalizedValue';
import { CoachExerciseBlock, createCoachExerciseBlock } from './CoachExerciseBlock';
import { languagePriority } from '../../../LocalizedEntity';
import { ExerciseVariation } from '../../../ProgramPortfolio/ExerciseVariation';
import { languageState } from '../../../../Contexts/LanguageContext';

export type CoachWorkoutPhase = {
  scriptId: string;
  phaseId: string;
  type: string;
  description: LocalizedValue[];
  exerciseBlocks: CoachExerciseBlock[];
  options: Record<string, string>;
  workout: CoachWorkout;
  hasReplaceSuggestions?: boolean;
  readonly totalExercises: number;
  readonly duration: number;
  readonly breakTime: number;
  readonly totalTimeExercising: number;
  readonly totalSets: number;
  readonly tons: number;
  readonly lastBlock: CoachExerciseBlock | undefined;
  readonly firstBlock: CoachExerciseBlock | undefined;
  readonly prevPhase: CoachWorkoutPhase | undefined;
  readonly nextPhase: CoachWorkoutPhase | undefined;
  readonly phaseIndex: number;
  readonly prevBlock: CoachExerciseBlock | undefined;
  readonly nextBlock: CoachExerciseBlock | undefined;
  readonly estimatedTotalExerciseTime: number;
  readonly translatedDescription: string;
  hasExercise(exercise: ExerciseVariation, exerciseBlockId?: string): boolean;
  replaceBlock(oldBlock: CoachExerciseBlock, newBlock: CoachExerciseBlock): void;
  calculateCalories(bmr24: number): number;
};

export function createCoachWorkoutPhase(workout: CoachWorkout, json?: Partial<CoachWorkoutPhase>): CoachWorkoutPhase {
  const phase: CoachWorkoutPhase = makeAutoObservable({
    scriptId: json?.scriptId ?? '',
    phaseId: json?.phaseId || UUID(),
    type: json?.type || 'strength',
    description: (json?.description ?? []).map((l) => createLocalizedValue(l)),
    exerciseBlocks: [] as CoachExerciseBlock[],
    options: json?.options || {},
    workout,
    hasReplaceSuggestions: undefined as boolean | undefined,

    get totalExercises(): number {
      return this.exerciseBlocks.length;
    },

    get duration(): number {
      return this.exerciseBlocks.reduce((total, block) => total + block.duration, 0);
    },

    get breakTime(): number {
      return this.exerciseBlocks.reduce((total, block) => total + block.breakTime, 0);
    },

    get totalTimeExercising(): number {
      return this.exerciseBlocks.reduce((total, block) => total + block.totalTimeExercising, 0);
    },

    get totalSets(): number {
      return this.exerciseBlocks.reduce((total, b) => total + b.sets.length, 0);
    },

    get tons(): number {
      return this.exerciseBlocks.reduce((total, b) => total + b.tons, 0);
    },

    get lastBlock(): CoachExerciseBlock | undefined {
      return this.exerciseBlocks.length > 0 ? this.exerciseBlocks[this.exerciseBlocks.length - 1] : undefined;
    },

    get firstBlock(): CoachExerciseBlock | undefined {
      return this.exerciseBlocks.length > 0 ? this.exerciseBlocks[0] : undefined;
    },

    get prevPhase(): CoachWorkoutPhase | undefined {
      if (this.workout) {
        const { phaseIndex } = this;
        return phaseIndex > 0 ? this.workout.phases[phaseIndex - 1] : undefined;
      }
      return undefined;
    },

    get nextPhase(): CoachWorkoutPhase | undefined {
      if (this.workout) {
        const nextIndex = this.phaseIndex + 1;
        return nextIndex < this.workout.phases.length ? this.workout.phases[nextIndex] : undefined;
      }
      return undefined;
    },

    get phaseIndex(): number {
      return this.workout ? this.workout.phases.findIndex((p) => p.phaseId === this.phaseId) : -1;
    },

    get prevBlock(): CoachExerciseBlock | undefined {
      return this.lastBlock || (this.prevPhase ? this.prevPhase.prevBlock : undefined);
    },

    get nextBlock(): CoachExerciseBlock | undefined {
      return this.firstBlock || (this.nextPhase ? this.nextPhase.nextBlock : undefined);
    },

    get estimatedTotalExerciseTime(): number {
      return this.exerciseBlocks.reduce((total, block) => total + block.estimatedTotalExerciseTime, 0);
    },

    get translatedDescription(): string {
      const language = languageState.language;
      const langFit = this.description.find((n) => n.lang === language.toLowerCase())?.value;

      if (langFit?.trim()) {
        return langFit;
      }

      for (const lang of languagePriority) {
        const entry = this.description.find((l) => l.lang === lang);
        if (entry) {
          return entry.value ?? '';
        }
      }
      return this.description[0]?.value ?? '';
    },

    hasExercise(exercise: ExerciseVariation, exerciseBlockId?: string): boolean {
      if (exerciseBlockId) {
        return !!this.exerciseBlocks.find((b) => b.exerciseBlockId === exerciseBlockId);
      }
      return this.exerciseBlocks.findIndex((b) => b.exercise.id === exercise.id) !== -1;
    },

    replaceBlock(oldBlock: CoachExerciseBlock, newBlock: CoachExerciseBlock): void {
      const index = this.exerciseBlocks.findIndex((b) => b.exerciseBlockId === oldBlock.exerciseBlockId);
      if (index !== -1) {
        this.exerciseBlocks[index] = newBlock;
      } else {
        this.exerciseBlocks.push(newBlock);
      }
    },

    calculateCalories(bmr24: number): number {
      return this.exerciseBlocks.reduce((total, b) => total + b.calculateCalories(bmr24), 0);
    },
  });

  phase.exerciseBlocks = (json?.exerciseBlocks || [])
    .filter((b: any) => b && b.exercise && b.exercise.id)
    .map((b: any) => createCoachExerciseBlock(phase, b));

  return phase;
}
